import dynamic from 'next/dynamic';

import { Button, Dialog, RadixDialogPrimitives } from '@capsule/consumer-design-system';

import { useHistory, useParams } from 'react-router';
import { css } from 'styled-components';

import useUnreadChatMessages from 'src/app/components/CapsuleChat/hooks/useUnreadChatMessages';
import { FailedOrderRescheduleLayout } from 'src/app/components/Pages/Order/FailedOrderReschedule/Layout/FailedOrderRescheduleLayout';
import type { RescheduleOrderPathParams } from 'src/app/components/Pages/Order/FailedOrderReschedule/types';
import { RescheduleOrderStepName } from 'src/app/components/Pages/Order/FailedOrderReschedule/types';
import { useGetOrder } from 'src/app/hooks/data/order';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { orderTracking, rescheduleOrder } from 'src/app/router/paths';
import { openChat } from 'src/app/state/slices/chat';
import { useDispatch } from 'src/app/state/store';

const MapCard = dynamic(
  () => import('src/app/components/Pages/Order/FailedOrderReschedule/MapCard').then(module => module.MapCard),
  { ssr: false }
);

const TrackEventViewVerifyFailedAddress = ({ delivery_address_id }: { delivery_address_id: string }) => {
  useSegmentEvent('viewVerifyFailedAddress', { shouldTrackOnMount: true, payload: { delivery_address_id } });
  return null;
};

export const InitialRescheduleFailedOrderStep = () => {
  const trackViewChatPrompt = useSegmentEvent('viewChatPrompt', { shouldTrackOnMount: false });
  const trackOpenChat = useSegmentEvent('openChat', { shouldTrackOnMount: false });

  const { hasUnreadMessages } = useUnreadChatMessages();

  const dispatch = useDispatch();
  const { push } = useHistory();
  const { orderId } = useParams<RescheduleOrderPathParams>();
  const { data: order } = useGetOrder(orderId);

  const goToAddDeliveryDetails = () =>
    push(rescheduleOrder.toPath({ orderId, stepName: RescheduleOrderStepName.VerifyAddress }));

  const openChangeAddressChat = () => {
    const orderItems =
      order?.items.map((item, index) => {
        const isLastItem = index === order.items.length - 1;
        return isLastItem ? `and ${item.name}` : `${item.name}`;
      }) ?? [];

    const formattedOrderItems = orderItems.length > 2 ? orderItems.join(', ') : orderItems.join(' ');

    push(orderTracking.toPath({ orderId }));
    dispatch(
      openChat({
        prepopulatedMessage: formattedOrderItems
          ? `Hi Capsule, I need to reschedule my order for ${formattedOrderItems} to a different address: `
          : `Hi Capsule, I need to reschedule my order to a different address: `,
        source: 'reschedule-order',
      })
    );
    trackOpenChat({ source: 'Reschedule change address', has_unread_message: hasUnreadMessages });
  };

  const delivery_address_id = order?.deliveryAddress?.id;

  return (
    <FailedOrderRescheduleLayout
      title="Please verify your delivery address"
      description="Our courier wasn't able to deliver your last order."
      cta1={{
        children: 'Deliver to this address',
        onClick: goToAddDeliveryDetails,
      }}
      cta2={
        <Dialog
          title="Change delivery address"
          description="Please chat with us to update your address."
          triggerButton={
            <Button
              onClick={() => trackViewChatPrompt({ source: 'Failed Delivery Address Update' })}
              variant="outline-lg"
              css={css`
                margin-top: ${({ theme }) => theme.space.s3};
              `}
            >
              Change address
            </Button>
          }
        >
          <RadixDialogPrimitives.Close asChild>
            <Button onClick={openChangeAddressChat} variant="contained-secondary-lg" isFullWidth>
              Open Chat
            </Button>
          </RadixDialogPrimitives.Close>
        </Dialog>
      }
    >
      {delivery_address_id && <TrackEventViewVerifyFailedAddress delivery_address_id={delivery_address_id} />}
      <MapCard deliveryAddress={order?.deliveryAddress} />
    </FailedOrderRescheduleLayout>
  );
};
